import { useState, useEffect, useRef, useCallback, useLayoutEffect, forwardRef, createElement } from 'react';
function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

/**
 * Hook which loads image and let us know the status
 * so we can show placeholder/fallback
 *
 * @returns status for image loading process
 */

function useImage(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
    src = _ref.src,
    srcSet = _ref.srcSet,
    sizes = _ref.sizes,
    crossOrigin = _ref.crossOrigin,
    ignorePlaceholder = _ref.ignorePlaceholder;
  var _useState = useState('idle'),
    status = _useState[0],
    setStatus = _useState[1];
  var isError = status === 'failed';
  var isLoading = status === 'loading';
  var isIdle = status === 'idle';
  var isLoaded = status === 'loaded';
  useEffect(function () {
    if (ignorePlaceholder) {
      return setStatus('loaded');
    }
    setStatus(src ? 'loading' : 'idle');
  }, [ignorePlaceholder, src]);
  var imageRef = useRef();
  var flush = function flush() {
    if (imageRef.current) {
      imageRef.current = null;
    }
  };
  var load = useCallback(function () {
    if (!src) return;
    flush();
    var img = new window.Image();
    img.src = src;
    if (crossOrigin) {
      img.crossOrigin = crossOrigin;
    }
    if (srcSet) {
      img.srcset = srcSet;
    }
    if (sizes) {
      img.sizes = sizes;
    }
    img.onload = function () {
      flush();
      setStatus('loaded');
    };
    img.onerror = function () {
      flush();
      setStatus('failed');
    };
    imageRef.current = img;
  }, [src, crossOrigin, srcSet, sizes]); // we want this effect to run synchronously before UI gets painted as we are working with dom api

  useLayoutEffect(function () {
    if (ignorePlaceholder) return;
    if (status === 'loading') {
      load();
    }
    return flush;
  }, [status, load, ignorePlaceholder]);
  return {
    status: status,
    isError: isError,
    isIdle: isIdle,
    isLoading: isLoading,
    isLoaded: isLoaded
  };
}
var _excluded = ["placeholderSrc", "placeholder", "placeholderColor", "ignorePlaceholder", "onLoad", "onError", "src", "loading", "crossOrigin", "height", "width"];
function getInt(x) {
  if (typeof x === 'number') {
    return x;
  }
  if (typeof x === 'string') {
    return parseInt(x, 10); // returns NaN if string does not starts with number
  }
  return undefined;
}
/**
 * React Component for images which shows
 * placeholder while image is loading
 */

var Image = /*#__PURE__*/forwardRef(function (props, ref) {
  var placeholderSrc = props.placeholderSrc,
    placeholder = props.placeholder,
    _props$placeholderCol = props.placeholderColor,
    placeholderColor = _props$placeholderCol === void 0 ? 'gray' : _props$placeholderCol,
    _props$ignorePlacehol = props.ignorePlaceholder,
    ignorePlaceholder = _props$ignorePlacehol === void 0 ? false : _props$ignorePlacehol,
    onLoad = props.onLoad,
    onError = props.onError,
    src = props.src,
    loading = props.loading,
    crossOrigin = props.crossOrigin,
    height = props.height,
    width = props.width,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var intWidth = getInt(width);
  var intHeight = getInt(height);
  var shared = _extends({
    ref: ref,
    width: width,
    height: height
  }, rest); // if loading (`lazy` | `eager`) is provided ignore adding placeholder

  var shouldIgnore = loading || ignorePlaceholder;
  var _useImage = useImage(_extends({}, props, {
      ignorePlaceholder: shouldIgnore
    })),
    isLoaded = _useImage.isLoaded;
  if (process.env.NODE_ENV !== 'production') {
    // throw error if src is not provided
    if (!src) {
      throw new Error("Image is missing required \"src\" property. Make sure you pass \"src\" in props to the `react-image-placeholder` component.");
    }
    if (!intHeight || !intWidth) {
      throw new Error("Image with src \"" + src + "\" must use unitless \"width\" and \"height\" properties.");
    }
  } // conditions for, `src` loading and if `src` fails

  if (!isLoaded) {
    if (placeholderSrc) return createElement("img", Object.assign({
      src: placeholderSrc
    }, shared)); // react element

    if (placeholder) return placeholder; // default placeholder if source and element is not provided
    // if styles are there in shared it can override

    var _shared$style = shared.style,
      style = _shared$style === void 0 ? {} : _shared$style;
    return createElement("div", Object.assign({}, shared, {
      style: _extends({
        height: intHeight + "px",
        width: intWidth + "px",
        background: placeholderColor
      }, style)
    }));
  } // shows up after loading of `src`

  return createElement("img", Object.assign({
    src: src,
    loading: loading,
    crossOrigin: crossOrigin,
    onLoad: onLoad,
    onError: onError
  }, shared));
});
export { Image, useImage };